export const AUDIO_LEVEL_ANALYSER_SETTINGS = {
  fftSize: 256,
  smoothingTimeConstant: 0.3,
  minDecibels: -80,
  maxDecibels: -60,
} as const;

// Speech-band FFT range + perceptual curve shared by the waveform bars and
// the single-value mic-level meters so the two agree. Skip DC / sub-bass
// (mains hum and HVAC rumble) and cap near the top of the voice band.
// The gamma lifts soft speech while the gain keeps loud audio pegged
// at the ceiling (intentionally exceeds 1 before clamping).
export const AUDIO_LEVEL_START_BIN = 5;
export const AUDIO_LEVEL_END_FACTOR = 5;
export const AUDIO_LEVEL_GAMMA = 0.5;
export const AUDIO_LEVEL_GAIN = 1.25;

/**
 * Collapses raw FFT frequency data (0–255 per bin) into a single perceptual
 * loudness value (0–1) over the speech band, matching the waveform bars.
 */
export const computeAudioLevel = (data: number[]): number => {
  if (data.length === 0) {
    return 0;
  }

  const endIndex = Math.floor(data.length / AUDIO_LEVEL_END_FACTOR);

  let sum = 0;
  let count = 0;

  for (let i = AUDIO_LEVEL_START_BIN; i < endIndex; i++) {
    sum += (data[i] ?? 0) / 255;
    count++;
  }

  const avg = count > 0 ? Math.min(sum / count, 1) : 0;
  return Math.min(Math.pow(avg, AUDIO_LEVEL_GAMMA) * AUDIO_LEVEL_GAIN, 1);
};
